import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";

import { AuthGuard } from "@/components/layout/AuthGuard";
import { Sidebar } from "@/components/layout/Sidebar";
import { TopBar } from "@/components/layout/TopBar";
import { useAuth } from "@/hooks/useAuth";
import { useThreads } from "@/hooks/useThreads";
import { useUser } from "@/hooks/useUser";
import { useSidebarStore } from "@/store/sidebarStore";

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  return (
    <AuthGuard>
      <AuthenticatedContent />
    </AuthGuard>
  );
}

function AuthenticatedContent() {
  const { signOut } = useAuth();
  const { user } = useUser();
  const { threads, createThread, deleteThread, updateTitle } = useThreads();
  const navigate = useNavigate();
  const sidebarMode = useSidebarStore((s) => s.mode);

  const recentThreads = (threads ?? []).slice(0, 5).map((t) => ({
    _id: t._id as string,
    title: t.title,
  }));

  function handleNewChat() {
    // If the most recent thread is empty (no messages), reuse it
    const latest = threads?.[0];
    if (latest && latest.lastMessageAt === latest.createdAt) {
      void navigate({ to: "/chat/$threadId", params: { threadId: latest._id as string } });
      return;
    }

    createThread({ title: "New Chat" })
      .then((threadId) => {
        void navigate({ to: "/chat/$threadId", params: { threadId: threadId as string } });
      })
      .catch(() => {});
  }

  return (
    <div className="min-h-screen bg-surface-light dark:bg-surface-dark">
      <Sidebar
        userId={user?._id}
        userName={user?.name ?? undefined}
        userImage={user?.image ?? undefined}
        recentThreads={recentThreads}
        onNewChat={handleNewChat}
        onSignOut={() => { void navigate({ to: "/" }); void signOut(); }}
        onDeleteThread={({ threadId }) => void deleteThread({ threadId: threadId as any })}
        onRenameThread={({ threadId, title }) => void updateTitle({ threadId: threadId as any, title })}
      />
      <main
        className={`flex min-h-screen flex-col lg:overflow-auto lg:transition-[margin] lg:duration-150 motion-reduce:lg:transition-none ${
          sidebarMode === "rail" ? "lg:ml-14" : "lg:ml-52"
        }`}
      >
        <TopBar onSignOut={() => { void navigate({ to: "/" }); void signOut(); }} onNewChat={handleNewChat} recentThreads={recentThreads} />
        {/* Reserve room on mobile for the fixed bottom action bar so normal-flow
            screens (inbox, tasks, settings…) clear it, and pad the top so their
            content clears the notch (viewport-fit=cover paints under it). The
            chat route is `fixed inset-0` and reserves its own space for both. */}
        <div className="flex-1 max-lg:pt-[env(safe-area-inset-top)] max-lg:pb-[calc(3.5rem+env(safe-area-inset-bottom))]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
